import { Building, Users, BarChart3, Settings, LogOut, Gift, History, User, TrendingUp, QrCode, FileText, Mail, Trophy, Heart } from "lucide-react"
import { NavLink, useLocation } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { useFollowUpNotifications } from "@/hooks/useFollowUpNotifications"
import { useSocialEngagementNotifications } from "@/hooks/useSocialEngagementNotifications"
import { useTenantFeatureAccess } from "@/hooks/useTenantFeatureAccess"

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"

export function AppSidebar() {
  const location = useLocation()
  const { user, userRole, signOut } = useAuth()
  const currentPath = location.pathname
  const { totalDue: dueFollowUpsCount } = useFollowUpNotifications()
  const { reachedCount, inactiveCount } = useSocialEngagementNotifications()
  const { hasVisitRewards, hasReferralRewards } = useTenantFeatureAccess()

  // Navigation items based on user role
  const getNavigationItems = () => {
    if (userRole === 'superadmin') {
      return [
        { title: "Übersicht", url: "/superadmin", icon: BarChart3 },
        { title: "Unternehmen", url: "/superadmin/unternehmen", icon: Building },
        { title: "Benutzer", url: "/superadmin/benutzer", icon: Users },
        { title: "System CRM", url: "/superadmin/crm", icon: Mail },
        { title: "Landing Pages", url: "/superadmin/landing-pages", icon: FileText },
        { title: "Empfehlungs-Vorlagen", url: "/superadmin/prämien-vorlagen", icon: Gift },
        { title: "Besucher-Vorlagen", url: "/superadmin/besucherprämien-vorlagen", icon: Gift },
        { title: "Soziales Engagement", url: "/superadmin/soziales-engagement", icon: Heart },
        { title: "Einstellungen", url: "/superadmin/einstellungen", icon: Settings },
      ]
    }
    
    if (userRole === 'admin') {
      return [
        { title: "Übersicht", url: "/admin", icon: BarChart3 },
        { title: "Unterstützer", url: "/admin/unterstuetzer", icon: Users },
        { title: "Empfehlungs-CRM", url: "/admin/referral-crm", icon: Mail },
        { title: "Landing Pages", url: "/admin/landing-pages", icon: FileText },
        /* TEMPORARILY HIDDEN: QR-Codes Tab - Code kept for future use
        { title: "QR-Codes", url: "/admin/qr-codes", icon: QrCode }, */
        { title: "Stempel", url: "/admin/stempel", icon: Building },
        { title: "Empfehlungsprämien", url: "/admin/belohnungen", icon: Gift },
        { title: "Besucherprämien", url: "/admin/besucherprämien", icon: Gift },
        { title: "Wettbewerbe", url: "/admin/wettbewerbe", icon: Trophy },
        { title: "Soziales Engagement", url: "/admin/soziales-engagement", icon: Heart },
        { title: "Analytik", url: "/admin/analytik", icon: BarChart3 },
        { title: "Einstellungen", url: "/admin/einstellungen", icon: Settings },
      ]
    }
    
    // Customer role - filter based on enabled features
    const customerItems = [
      { title: "Übersicht", url: "/kunde", icon: BarChart3 },
      { title: "Wettbewerbe", url: "/kunde/wettbewerbe", icon: Trophy },
      { title: "Soziales Engagement", url: "/kunde/soziales-engagement", icon: Heart },
    ]
    
    // Add feature-specific items only if enabled
    if (hasReferralRewards) {
      customerItems.splice(1, 0, { title: "Empfehlungsprämien", url: "/kunde/praemien", icon: Gift })
    }
    if (hasVisitRewards) {
      customerItems.splice(hasReferralRewards ? 2 : 1, 0, { title: "Besucherprämien", url: "/kunde/besucherprämien", icon: Gift })
    }
    
    return customerItems
  }

  const items = getNavigationItems()

  // Show loading state if user role is still being fetched
  if (!user || !userRole) {
    return (
      <Sidebar className="w-14 sm:w-56 md:w-64">
        <SidebarContent>
          <div className="p-2 sm:p-3 md:p-4 text-center text-muted-foreground text-xs sm:text-sm">
            Laden...
          </div>
        </SidebarContent>
      </Sidebar>
    )
  }

  const getRoleLabel = () => {
    switch (userRole) {
      case 'superadmin': return 'Super Administrator'
      case 'admin': return 'Administrator'
      case 'customer': return 'Kunde'
      default: return 'Benutzer'
    }
  }

  const getUserInitials = () => {
    if (!user?.email) return 'U'
    return user.email.substring(0, 2).toUpperCase()
  }

  return (
    <Sidebar className="w-14 sm:w-56 md:w-64">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-sm sm:text-base md:text-lg font-semibold mb-2 sm:mb-3 md:mb-4">
            <div className="flex items-center gap-1 sm:gap-2">
              <div className="bg-gradient-to-r from-primary to-primary-glow p-1 sm:p-1.5 md:p-2 rounded-md">
                <Building className="h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 text-primary-foreground" />
              </div>
              <span className="hidden sm:inline text-xs sm:text-sm md:text-base">Herzensempfehlung</span>
            </div>
          </SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu className="space-y-0.5 sm:space-y-1">
              {items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to={item.url} 
                      end 
                      className={(isActive) =>
                        `w-full justify-start text-xs sm:text-sm ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-accent hover:text-accent-foreground"}`
                      }
                    >
                      <item.icon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
                      <span className="hidden sm:inline truncate">{item.title}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2 sm:p-3 md:p-4">
        <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2 md:mb-3">
          <Avatar className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8">
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 hidden sm:block">
            {userRole === 'customer' ? (
              <NavLink 
                to="/kunde/profil"
                className="block hover:text-primary transition-colors"
              >
                <p className="text-xs md:text-sm font-medium truncate cursor-pointer">
                  {user?.email}
                </p>
              </NavLink>
            ) : (
              <p className="text-xs md:text-sm font-medium truncate">
                {user?.email}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {getRoleLabel()}
            </p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={signOut}
          className="w-full justify-start text-xs sm:text-sm p-1 sm:p-2"
        >
          <LogOut className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Abmelden</span>
        </Button>
      </SidebarFooter>
    </Sidebar>
  )
}